import { FieldErrors } from 'react-hook-form';

/**
 * Interface para erro de validação detalhado
 */
export interface ValidationErrorDetail {
    field: string;
    message: string;
    humanReadableField: string;
}

/**
 * Mapeamento de campos para nomes legíveis em português
 */
const FIELD_LABELS: Record<string, string> = {
    // Dados Pessoais
    'name': 'Nome',
    'personalEmail': 'E-mail pessoal',
    'phone': 'Telefone',
    'birthDate': 'Data de nascimento',
    'sex': 'Sexo',
    'race': 'Raça/Etnia',
    'gender': 'Gênero',
    'nationality': 'Nacionalidade',
    'naturality': 'Naturalidade',
    'birthState': 'Estado de nascimento',
    'maritalStatus': 'Estado civil',

    // Endereços
    'addresses.0.fullAddress': 'Endereço principal',
    'addresses.0.number': 'Número do endereço principal',
    'addresses.0.complement': 'Complemento do endereço principal',
    'addresses.0.proofOfAddressFile': 'Comprovante de endereço',
    'addresses.1.fullAddress': 'Endereço de correspondência',
    'addresses.1.number': 'Número do endereço de correspondência',
    'addresses.1.complement': 'Complemento do endereço de correspondência',

    // Documentos
    'cpf': 'CPF',
    'identityDocument.number': 'Número do documento',
    'identityDocument.issuer': 'Órgão emissor',
    'identityDocument.uf': 'UF do documento',
    'identityDocument.issueDate': 'Data de expedição',
    'identityDocument.idDocumentFrontUrl': 'Documento (frente)',
    'identityDocument.idDocumentBackUrl': 'Documento (verso)',

    // Dados bancários
    'bankAccount.bank': 'Banco',
    'bankAccount.agency': 'Agência',
    'bankAccount.account': 'Conta',
    'bankAccount.accountType': 'Tipo de conta',

    // Dados de contratação
    'positionId': 'Cargo',
    'seniorityId': 'Senioridade',
    'departmentId': 'Departamento',
    'roleType': 'Atuação',
    'collaboratorType': 'Tipo de vínculo',
    'startDate': 'Data de início',
    'endDate': 'Data de fim',
    'workSchedule': 'Carga horária',
    'shirtSize': 'Tamanho da camisa',
    'slipperSize': 'Tamanho do chinelo',

    // Emergência
    'emergencyContact.name': 'Nome do contato de emergência',
    'emergencyContact.phone': 'Telefone do contato de emergência',
    'emergencyContact.relationship': 'Parentesco do contato de emergência',

    // Férias
    'vacationPeriods': 'Períodos de férias',
};

/**
 * Obtém o nome legível de um campo
 */
export const getFieldLabel = (fieldPath: string): string => {
    // Primeiro, tentar match exato
    if (FIELD_LABELS[fieldPath]) {
        return FIELD_LABELS[fieldPath];
    }

    // Para arrays, tentar padrões como vacationPeriods.0.startDate
    const arrayFieldMatch = fieldPath.match(/^(\w+)\.(\d+)\.(\w+)$/);
    if (arrayFieldMatch) {
        const [, arrayName, index, subField] = arrayFieldMatch;
        const baseKey = `${arrayName}.${subField}`;
        if (FIELD_LABELS[baseKey]) {
            return `${FIELD_LABELS[baseKey]} (item ${parseInt(index) + 1})`;
        }
    }

    // Para objetos aninhados, tentar padrões como addresses.0.fullAddress
    const nestedMatch = fieldPath.match(/^(\w+)\.(\d+)\.(\w+)$/);
    if (nestedMatch) {
        const constructedKey = `${nestedMatch[1]}.${nestedMatch[2]}.${nestedMatch[3]}`;
        if (FIELD_LABELS[constructedKey]) {
            return FIELD_LABELS[constructedKey];
        }
    }

    // Fallback: capitalizar e substituir underscores/dots
    return fieldPath
        .replace(/([A-Z])/g, ' $1')
        .replace(/[._]/g, ' ')
        .toLowerCase()
        .replace(/^\w/, c => c.toUpperCase())
        .trim();
};

/**
 * Extrai erros detalhados do React Hook Form
 */
export const extractValidationErrors = (errors: FieldErrors<any>): ValidationErrorDetail[] => {
    const extractedErrors: ValidationErrorDetail[] = [];

    const extractFromObject = (errorObj: any, prefix = '') => {
        Object.keys(errorObj).forEach(key => {
            const fullKey = prefix ? `${prefix}.${key}` : key;
            const error = errorObj[key];

            if (error && typeof error === 'object') {
                if (error.message) {
                    // É um erro direto
                    extractedErrors.push({
                        field: fullKey,
                        message: error.message,
                        humanReadableField: getFieldLabel(fullKey)
                    });
                } else if (Array.isArray(error)) {
                    // É um array de erros
                    error.forEach((item, index) => {
                        if (item && typeof item === 'object') {
                            extractFromObject(item, `${fullKey}.${index}`);
                        }
                    });
                } else {
                    // É um objeto aninhado
                    extractFromObject(error, fullKey);
                }
            }
        });
    };

    extractFromObject(errors);
    return extractedErrors;
};

/**
 * Formata lista de erros para exibição em toast
 */
export const formatErrorsForToast = (errors: ValidationErrorDetail[]): string => {
    if (errors.length === 0) return 'Erro de validação desconhecido';

    if (errors.length === 1) {
        return `Você tem 1 erro no formulário. Verifique os campos destacados.`;
    }

    return `Você tem ${errors.length} erros no formulário. Verifique os campos destacados.`;
};

/**
 * Formata erros para console (debug)
 */
export const formatErrorsForConsole = (errors: ValidationErrorDetail[]): void => {
    console.group('❌ ERROS DE VALIDAÇÃO DETALHADOS');
    console.log(`Total de erros: ${errors.length}`);
    console.table(errors.map(error => ({
        Campo: error.field,
        'Nome Legível': error.humanReadableField,
        Mensagem: error.message
    })));
    console.groupEnd();
}; 